import * as React from 'react';
import { WithSearch } from "@elastic/react-search-ui";
import { buildData, buildBandData, bandAreaGroupsOf, Config, withXAxis, percentileBase, withPercentileFallback, normalizeSearchResults } from "../../helpers";
import { LinesPreloader } from "../Preloader";
import LineChartWidget from "../LineChartWidget";
import { useChartBridge } from "../ChartBridgeContext";

const NO_RESULTS_DELAY_MS = 400;

function Chart({ results, wasSearched, isLoading, chartConfig, lineStyle }) {
  const { setOptions, selected, setSelected, setSubtitle, initialSelected } = useChartBridge();

  const normalized = React.useMemo(
    () => normalizeSearchResults(results, wasSearched),
    [results, wasSearched]
  );

  // Grouped dropdown filter — drives subtitle and isDynamic detection.
  const groupedFilter = React.useMemo(
    () => (chartConfig.filters || []).find(f => f.kind === 'grouped-dropdown'),
    [chartConfig]
  );

  // Axis metric switcher options come from the axis-switcher filter's explicit
  // options list. Each entry is { label, field } from the settings form. The
  // internal shape adds `value` (= field, stable identifier) and `percentile`
  // (numeric, for withPercentileFallback — null when field doesn't follow p<N>_*).
  const axisFilter = React.useMemo(
    () => (chartConfig.filters || []).find(f => f.kind === 'axis-switcher'),
    [chartConfig]
  );

  const options = React.useMemo(() => {
    const configOptions = axisFilter?.options;
    if (!configOptions?.length) return [];
    // Dynamic-only: when the switcher names a showWhenField, only surface it while
    // the current rows carry that field (the agentic/Dynamic selection). A Fixed
    // ISL/OSL selection has no distribution — it keeps the configured scalar X and
    // shows no switcher. This states the rule the chart actually wants; the
    // per-field data check below only approximates it, and diverges for a Fixed
    // row that happens to carry two percentiles (allowed by the v4 schema).
    const gate = axisFilter.showWhenField;
    if (gate && !normalized.some(row => row?.[gate] != null && row[gate] !== '')) return [];
    // Surface only the axis options whose field is actually populated in the
    // current rows. An empty option would plot nothing, and — via the band edge
    // lookup below — would let a band be offered whose edge has no data (its fill
    // then silently collapses). Keeping the option list data-driven, per field,
    // restores the pre-generalization behavior.
    return configOptions
      .filter(opt => normalized.some(row => row?.[opt.field] != null))
      .map(opt => {
        const m = /^p(\d+(?:_\d+)?)_/.exec(opt.field);
        const percentile = m ? parseFloat(m[1].replace('_', '.')) : null;
        return { value: opt.field, field: opt.field, label: opt.label, percentile };
      });
  }, [axisFilter, normalized]);
  const optionsKey = options.map(o => o.value).join(',');

  // Short display label for an axis option — the author-set label, else a
  // derived "P<n>" for a percentile field, else the raw field name. Used for
  // band edges, legend suffixes and the export subtitle.
  const shortLabel = (o) => o?.label || (o?.percentile != null ? `P${o.percentile}` : (o?.field ?? ''));

  // X axis band: when configured, adds an extra "band" option that draws a
  // shaded region between two fields. The Low/High values are field names that
  // must match entries in the Axis metric switcher options list.
  const bandConfig = chartConfig.axes?.x?.band;
  const bandOption = React.useMemo(() => {
    const mode = bandConfig?.mode;
    if (mode !== 'lines' && mode !== 'area') return null;
    // Opt-in gate: when band.showWhenField names a field, only offer the band
    // while the current rows carry it (the agentic/Dynamic selection) — a Fixed
    // ISL/OSL selection has no interactivity distribution to shade.
    const gateField = bandConfig.showWhenField;
    if (gateField && !normalized.some(row => row?.[gateField] != null && row[gateField] !== '')) return null;
    const tokens = bandConfig?.fields || [];
    if (tokens.length !== 2 || !tokens[0] || !tokens[1]) return null;
    // Both edges must also be live options — `options` is already filtered to
    // fields with data, so an edge with no data cannot produce a collapsed band.
    const lo = options.find(o => o.value === tokens[0]);
    const hi = options.find(o => o.value === tokens[1]);
    if (!lo || !hi) return null;
    return {
      value: 'band', isBand: true, mode, lo, hi, field: lo.field,
      label: bandConfig.label || `${shortLabel(lo)}–${shortLabel(hi)} band`,
    };
  }, [bandConfig, optionsKey, normalized]);

  const pickerOptions = React.useMemo(
    () => (bandOption ? [...options, bandOption] : options),
    [options, bandOption]
  );
  const pickerKey = pickerOptions.map(o => o.value).join(',');

  React.useEffect(() => { setOptions(pickerOptions); }, [pickerKey]);

  // The shared-permalink axis (§1.4), held until it becomes selectable: the
  // picker options follow the model/configuration (restored async via setFilter),
  // so it can be transiently absent while they resolve, and we must not snap to a
  // default in the meantime. Seeded from the permalink value, NOT live `selected`
  // — this component remounts on every Chart/Table toggle, so reading `selected`
  // would re-capture the current pick and pin it permanently.
  const sharedAxis = React.useRef(initialSelected);

  React.useEffect(() => {
    if (pickerOptions.length === 0) return;
    // Release the hold once the shared value is actually selectable, so normal
    // defaulting resumes if a later selection no longer offers it.
    if (sharedAxis.current && pickerOptions.some(o => o.value === sharedAxis.current)) {
      sharedAxis.current = null;
    } else if (sharedAxis.current && selected === sharedAxis.current) {
      return;
    }
    if (!selected || !pickerOptions.some(o => o.value === selected)) {
      // Default to the configured X percentile when present, else the lowest —
      // never the band (opt-in only).
      const initial = pickerOptions.find(o => o.field === chartConfig.axes?.x?.field)?.value ?? pickerOptions[0].value;
      setSelected(initial);
    }
  }, [pickerKey]);

  const selectedOption = React.useMemo(
    () => pickerOptions.find(o => o.value === selected) || null,
    [pickerOptions, selected]
  );

  const effectiveConfig = React.useMemo(() => {
    if (!selectedOption) return chartConfig;
    // For a band the lo edge field carries the shared numeric x grid.
    const field = selectedOption.isBand ? selectedOption.lo.field : selectedOption.field;
    return withXAxis(chartConfig, field);
  }, [chartConfig, selectedOption]);

  // Chart subtitle "<primary> | <group> | (<metric>)" for the exported image —
  // derived from the filtered rows + selected option, published to the context
  // so the export controls (in ChartLayout) can read it. Empty segments are
  // dropped, so a chart without a grouped dropdown or axis switcher still
  // produces a sensible subtitle.
  const subtitle = React.useMemo(() => {
    if (normalized.length === 0) return '';
    const row = normalized[0];
    const valueLabels = groupedFilter?.valueLabels || {};
    const sections = groupedFilter?.sections || [];
    // Primary label: the grouped dropdown's scope field (the entity the chart is
    // about, e.g. model), preferring a `<field>_display` variant when present.
    const scopeField = groupedFilter?.scopeAttribute;
    const primary = scopeField ? (row[`${scopeField}_display`] || row[scopeField] || '') : '';
    // Group label: first section that has a populated value in this row.
    const groupValue = sections.reduce((found, s) => {
      if (found) return found;
      const v = row[s.field];
      return (v != null && v !== '') ? (valueLabels[v] || String(v).replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())) : null;
    }, null) || '';
    let metric = '';
    if (selectedOption?.isBand) {
      metric = `(${shortLabel(selectedOption.lo)}–${shortLabel(selectedOption.hi)})`;
    } else if (selectedOption) {
      metric = `(${shortLabel(selectedOption)})`;
    }
    return [primary, groupValue, metric].filter(Boolean).join(' | ');
  }, [normalized, groupedFilter, selectedOption]);

  React.useEffect(() => { setSubtitle(subtitle); }, [subtitle]);

  // Per-row percentile fallback (§1.5): with a percentile selected, fill each
  // row's chosen field from the nearest it carries so a point isn't dropped
  // just because that exact percentile is missing.
  const chartRows = React.useMemo(() => {
    if (!selectedOption) return normalized;
    if (selectedOption.isBand) {
      // Fill each edge independently so a point isn't dropped for a missing exact
      // percentile. The base comes from the edge's own field (e.g. p90_intvty ->
      // intvty), so it holds whatever the configured default X is.
      const rows = withPercentileFallback(normalized, percentileBase(selectedOption.lo.field), selectedOption.lo.percentile, selectedOption.lo.field);
      return withPercentileFallback(rows, percentileBase(selectedOption.hi.field), selectedOption.hi.percentile, selectedOption.hi.field);
    }
    return withPercentileFallback(normalized, percentileBase(selectedOption.field), selectedOption.percentile, selectedOption.field);
  }, [normalized, selectedOption]);

  const { data: chartData, groups: chartGroups } = React.useMemo(() => {
    if (chartRows.length === 0) return { data: [], groups: [] };
    if (selectedOption?.isBand) {
      return buildBandData(chartRows, effectiveConfig, [
        { field: selectedOption.lo.field, band: 'lo', labelSuffix: ` (${shortLabel(selectedOption.lo)})` },
        { field: selectedOption.hi.field, band: 'hi', labelSuffix: ` (${shortLabel(selectedOption.hi)})` },
      ]);
    }
    return buildData(chartRows, effectiveConfig, { frontier: !!chartConfig.pareto?.frontierOnly });
  }, [chartRows, effectiveConfig, selectedOption]);

  // Shaded band fill geometry (band.mode:"area"): lo/hi curves per group, drawn
  // under the edge lines by LineChartWidget's <Customized> layer.
  const bandArea = React.useMemo(() => {
    if (!selectedOption?.isBand || selectedOption.mode !== 'area') return null;
    return bandAreaGroupsOf(chartRows, effectiveConfig, selectedOption.lo.field, selectedOption.hi.field);
  }, [selectedOption, chartRows, effectiveConfig]);

  // Delay committing to "no results" — a cascade correction (e.g., replacing a
  // count-0 sequence length after a model change) briefly lands a zero-hit
  // response before the follow-up search starts; without this delay that gap
  // flashes the empty message.
  const [emptyCommitted, setEmptyCommitted] = React.useState(false);
  React.useEffect(() => {
    if (isLoading || results.length > 0) {
      setEmptyCommitted(false);
      return;
    }
    const handle = setTimeout(() => setEmptyCommitted(true), NO_RESULTS_DELAY_MS);
    return () => clearTimeout(handle);
  }, [isLoading, results]);

  if (!wasSearched) return null;
  if (isLoading || (results.length === 0 && !emptyCommitted)) return <LinesPreloader/>;
  if (results.length === 0) return <p className='h--smaller mb-4'>{Config.I18n.Hits.NoResults}</p>;
  if (chartData.length === 0 || chartGroups.length === 0) return <LinesPreloader/>;

  return <LineChartWidget chartData={chartData} chartGroups={chartGroups} chartConfig={effectiveConfig} lineType={lineStyle} bandArea={bandArea}/>;
}

export default function HitsWidget({ chartConfig, lineStyle }) {
  return (
    <WithSearch mapContextToProps={({ results, wasSearched, isLoading }) => ({ results, wasSearched, isLoading })}>
      {({ results, wasSearched, isLoading }) => (
        <Chart results={results} wasSearched={wasSearched} isLoading={isLoading} chartConfig={chartConfig} lineStyle={lineStyle}/>
      )}
    </WithSearch>
  );
}
